import { AlertTriangle } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

// Deal Pipeline spec §6.5 — archive is blocked when the counterparty is
// linked to an active deal. The API enforces the check (PersonService.DeleteAsync
// and its peers answer CONFLICT 409), and this modal fires on that 409.

interface ActiveDealsBlockedDialogProps {
  open: boolean;
  /** Display name of the counterparty being archived. */
  displayName: string;
  /** Optional list of deal references; populated when the API returns
   *  them. The dialog falls back to a generic message when the list is empty. */
  activeDealRefs?: { id: string; reference: string }[];
  onClose: () => void;
}

export function ActiveDealsBlockedDialog({
  open,
  displayName,
  activeDealRefs = [],
  onClose,
}: ActiveDealsBlockedDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Archive blocked — active deals"
      size="sm"
      footer={<Button onClick={onClose}>Got it</Button>}
    >
      <div className="flex flex-col gap-space-3">
        <div className="flex items-start gap-space-2 text-small text-status-warning">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <p className="m-0 text-gray-dark">
            <span className="font-semibold text-onyx">{displayName}</span> can't be archived while
            it's linked to one or more active deals. Resolve those deals first, then try again.
          </p>
        </div>
        {activeDealRefs.length > 0 ? (
          <ul className="flex flex-col gap-space-1 border border-border-light rounded-md p-space-3">
            {activeDealRefs.map((d) => (
              <li key={d.id} className="text-small font-mono">
                {d.reference}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-gray-mid m-0">
            See the Deal Pipeline for the active references. (Detailed deal links arrive in a
            follow-up release.)
          </p>
        )}
      </div>
    </Modal>
  );
}
